import { getPlayerOptions } from '../util';
import { Path } from './config.js';

/**
 * @typedef {Object} PlayerOptions
 * @property {string} userNameHash
 * @property {boolean} enableConviva
 * @property {string} playbackTicket
 * @property {boolean} allowPopout
 * @property {boolean} showMainControls
 * @property {boolean} allowScrubbing
 * @property {boolean} allowPlayPauseStop
 * @property {boolean} enablePresentationInfo
 * @property {boolean} showDateTime
 * @property {boolean} allowClosedCaptions
 * @property {boolean} allowFullscreen
 */

/**
 * 
 * @param {string} presentationId 
 * @returns {PlayerOptions}
 */
export function getOptions(presentationId) {
    return getPlayerOptions(presentationId).then(presentationOptions => {
        let { GlobalOptions, NextGenPlayerOptions, Presentation } = presentationOptions.d;

        if (GlobalOptions?.IsLayoutPreview) {
            //Set these values
            Presentation = {
                HasSearchableText: true,
                ForumEnabled: true,
                SharingEnabled: true,
                PollingEnabled: true,
                IsDvrPlaybackEnabled: false
            }
        }

        let browserSupportsFullscreenElement = typeof (document.body.requestFullscreen ?? document.body.webkitRequestFullscreen) === 'function';
        let enableFullScreen = browserSupportsFullscreenElement && (NextGenPlayerOptions?.AllowFullscreen ?? true);
        let userCanDownload = GlobalOptions?.CanDownloadMediasiteContent || GlobalOptions?.IsPresentationOwner;
        let streamWithThumbs = Presentation?.Streams?.find((s) => s.HasPreviewThumbs);
        let hasMultipleStreams = (Presentation?.Streams?.length ?? 0) > 1;
        let hasMultipleVisualStreams = (Presentation?.Streams?.filter(s => !s.AudioOnly).length ?? 0) > 1;

        let result = {
            conviva: {
                enabled: GlobalOptions?.EnableConviva,
                userNameHash: GlobalOptions?.UserNameHash,
                convivaServiceUrl: GlobalOptions?.ConvivaServiceUrl,
                convivaCustomerId: GlobalOptions?.ConvivaCustomerId,
                playerType: NextGenPlayerOptions?.PlayerType,
                playerId: GlobalOptions?.PlayerId,
                playerName: GlobalOptions?.PlayerName,
                layoutId: GlobalOptions?.LayoutId,
                siteName: GlobalOptions?.SiteName,
                siteVersion: GlobalOptions?.SiteVersion,
                siteBuildNumber: GlobalOptions?.SiteBuildNumber,
                multisiteId: GlobalOptions.MultisiteId
            },

            reporting: {
                playbackTicket: GlobalOptions?.PlaybackTicket
            },

            inactivity: {
                sessionTimeout: GlobalOptions?.SessionTimeout
            },

            metadata: {
                showControlBarButton: NextGenPlayerOptions?.EnablePresentationInfo ?? true,
                showPublishDateTime: NextGenPlayerOptions?.ShowDateTime ?? true
            },

            chapters: {
                enabled: NextGenPlayerOptions?.AllowChapterNavigation ?? true,
                showChapterList: NextGenPlayerOptions?.AllowScrubbing ?? true //if seeking is disabled, remove the chapter list from the UI
            },

            //if seeking is disabled, so should search so that the user can't jump around the video through searching
            search: {
                enabled: NextGenPlayerOptions?.EnableSearch === true && NextGenPlayerOptions?.AllowScrubbing === true && Presentation?.HasSearchableText === true
            },

            popout: {
                enabled: NextGenPlayerOptions?.AllowPopout === true
            },
            
            controls: NextGenPlayerOptions?.ShowMainControls ?? true,

            settings: {
                enablePlayPause: NextGenPlayerOptions?.AllowPlayPauseStop ?? true,
                enableFullScreen: enableFullScreen,
                enableSpeedControl: NextGenPlayerOptions?.AllowSpeedControl ?? true,
                enableSeeking: NextGenPlayerOptions?.AllowScrubbing ?? true,
                enableSwapStreams: hasMultipleVisualStreams && !(NextGenPlayerOptions?.HideSwapStreamsButton),
                playbackRates: NextGenPlayerOptions?.AllowSpeedControl ? getPlaybackRates(GlobalOptions?.PlaybackSpeeds) : []
            },

            live: {
                enableDvr: (Presentation?.IsDvrPlaybackEnabled ?? false) && NextGenPlayerOptions?.AllowScrubbing,
                enableLiveCaptions: GlobalOptions?.LiveCaptioningEnabled ?? false
            },

            askAQuestion: {
                enabled: (NextGenPlayerOptions?.AllowAskQuestion && Presentation?.ForumEnabled) ?? false
            },

            downloads: {
                enabled: (NextGenPlayerOptions?.AllowDownloads && userCanDownload) ?? false
            },

            share: {
                enabled: NextGenPlayerOptions?.AllowShare === true && Presentation?.SharingEnabled !== false
            },

            polls: {
                enabled: NextGenPlayerOptions?.AllowPolls && Presentation?.PollingEnabled,
                url: Presentation?.PollingLocation
            },

            help: {
                url: NextGenPlayerOptions?.AllowHelp ? (GlobalOptions?.PremierPlayerHelpUrl ?? null) : null
            },

            banner: {
                enabled: NextGenPlayerOptions?.ShowHeaderArea == 'Disabled' ? false : NextGenPlayerOptions?.FullScreenBannerPath ? true : false,
                renderIn360: Presentation?.Is360Video,
                src: NextGenPlayerOptions?.FullScreenBannerPath,
                href: NextGenPlayerOptions?.FullScreenBannerUrl,
                width: NextGenPlayerOptions?.FullScreenBannerWidth,
                height: NextGenPlayerOptions?.FullScreenBannerHeight,
                behavior: NextGenPlayerOptions?.ShowHeaderArea != 'Disabled' ? NextGenPlayerOptions?.ShowHeaderArea : 'Disabled',
                endedEnabled: NextGenPlayerOptions?.PresentationEndedBannerEnabled,
                endedSrc: NextGenPlayerOptions?.PresentationEndedBannerPath,
                endedHref: NextGenPlayerOptions?.PresentationEndedBannerUrl,
                endedWidth: NextGenPlayerOptions?.PresentationEndedBannerWidth,
                endedHeight: NextGenPlayerOptions?.PresentationEndedBannerHeight
            },

            seekThumbnails: {
                enabled: (streamWithThumbs?.HasPreviewThumbs && NextGenPlayerOptions?.AllowScrubbing) ?? false,
                thumbData: streamWithThumbs?.PreviewThumbs
            },

            background: {
                isMultiStream: hasMultipleStreams,
                compositionMap: Presentation?.CompositionMap,
            }
        }

        return result;
    });
}

/**
 * @param {string} presentationId
 * @returns {Promise<Array<PlayerExtensionConfig>>}
 */
export function getExtensions(presentationId) {
    return getPlayerOptions(presentationId).then(playerOptions => {
        const { Presentation } = playerOptions.d;
        const extensions = [...Presentation?.PlayerExtensions ?? []];
        return extensions;
    });
}

/**
 * @typedef {Object} LiveEvent
 * @property {number} eventType
 * @property {number} time
 */

/**
 * 
 * @param {string} playbackTicket
 * @param {number} [currentTimeInMs]
 * @param {number} [sinceTimeInMs]
 * @returns {Promise<Array<LiveEvent>>}
 */
export function getLiveEvents(playbackTicket, currentTimeInMs = 0, sinceTimeInMs = -1) {
    return fetch(`${Path.PlayerService}/GetLiveEvents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({
            playbackTicket,
            currentTimeInMs,
            sinceTimeInMs
        })
    }).then(result => result.json()).then(jsonData => jsonData.d.map(event => ({
        eventType: event.EventType,
        id: event.Id,
        payload: event.Payload,
        payloadType: event.PayloadType,
        sequence: event.Sequence,
        time: event.Time
    })));
}

/**
 * 
 * @param {string} playbackTicket 
 * @param {number} bookmarkPosition 
 * @param {Array<{Duration:number, StartTime: number}>} segments 
 */
export function reportMediaView(playbackTicket, bookmarkPosition, segments) {
    return fetch(`${Path.PlayerService}/ReportMediaView`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({
            playbackTicket,
            bookmarkPosition,
            segments
        })
    });
}

export function reportMediaOpen(playbackTicket) {
    const embeddedPlayer = window.self != window.top;
    let container = null;
    if (embeddedPlayer) {
        try {
            let containerUrl = window.parent.document.location.href;
            if (containerUrl) container = containerUrl;
        } catch { }
    }
    return fetch(`${Path.PlayerService}/ReportMediaOpen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({
            playbackTicket,
            playerType: 'Javascript',
            mediaPlayerType: 'Html5',
            embeddedPlayer,
            url: document.location.href,
            container
        })
    });
}

function getPlaybackRates(playbackSpeedsString) {
    const defaultList = [0.5, .75, 1.0, 1.25, 1.5, 1.75, 2.0];

    if (typeof playbackSpeedsString !== 'string') {
        return defaultList;
    }

    let parsedList = parsePlaybackSpeedsString(playbackSpeedsString);

    if (parsedList.length < 1) {
        return defaultList;
    }

    return parsedList;
}

function parsePlaybackSpeedsString(theString) {
    const pattern = /[\d\.]+/g;

    if (typeof theString !== 'string') {
        return [];
    }

    let matches = theString.match(pattern);

    if (!matches) {
        return [];
    }


    const results = [];

    for (let i = 0; i < matches.length; i += 1) {
        let num = Number(matches[i]);
        if (!isNaN(num)) {
            results.push(num);
        }
    }

    return results;
}